import {
    Typography as TypographyBase,
    TypographyProps as TypographyBaseProps,
} from '@mui/material';
import React, { CSSProperties } from 'react';
import { ThemePalette } from '../style/theme';

export type TypographyRole =
    | 'heading1'
    | 'heading2'
    | 'heading3'
    | 'heading4'
    | 'body'
    | 'body-small'
    | 'body-bold'
    | 'body-small-bold'
    | 'label'
    | 'label-light'
    | 'label-bold'
    | 'label-large'
    | 'label-large-light'
    | 'label-large-bold'
    | 'label-small'
    | 'label-small-bold'
    | 'label-enclosed'
    | 'label-enclosed-bold'
    | 'label-enclosed-small-bold'
    | 'button'
    | 'button-small'
    | 'tooltip-title'
    | 'tooltip-body';

export type TypographyProps = Pick<
    TypographyBaseProps,
    'sx' | 'align' | 'noWrap' | 'whiteSpace'
> & {
    role: TypographyRole;
    color?:
        | keyof Omit<ThemePalette['text'], 'getBase' | 'getPressed' | 'getDisabled' | 'getOn'>
        | 'inherit';
    children: React.ReactNode;
};

export const Typography: React.FC<TypographyProps> = ({
    role,
    color = 'default',
    sx,
    noWrap,
    align,
    whiteSpace,
    children,
}) => {
    let fontSize: CSSProperties['fontSize'];
    let lineHeight: CSSProperties['lineHeight'];
    let fontWeight: CSSProperties['fontWeight'];
    switch (role) {
        case 'heading1':
            fontSize = 24;
            lineHeight = 1.5;
            fontWeight = '600';
            break;
        case 'heading2':
            fontSize = 20;
            lineHeight = 1.5;
            fontWeight = '600';
            break;
        case 'heading3':
            fontSize = 18;
            lineHeight = 1.35;
            fontWeight = '600';
            break;
        case 'heading4':
            fontSize = 14;
            lineHeight = 1.45;
            fontWeight = '500';
            break;
        case 'body':
            fontSize = 16;
            lineHeight = 1.5;
            fontWeight = '400';
            break;
        case 'body-small':
            fontSize = 14;
            lineHeight = 1.45;
            fontWeight = '400';
            break;
        case 'body-bold':
            fontSize = 16;
            lineHeight = 1.5;
            fontWeight = '500';
            break;
        case 'body-small-bold':
            fontSize = 14;
            lineHeight = 1.45;
            fontWeight = '500';
            break;
        case 'label':
            fontSize = 14;
            lineHeight = 1.45;
            fontWeight = '500';
            break;
        case 'label-light':
            fontSize = 14;
            lineHeight = 1.45;
            fontWeight = '400';
            break;
        case 'label-bold':
            fontSize = 14;
            lineHeight = 1.45;
            fontWeight = '600';
            break;
        case 'label-large':
            fontSize = 16;
            lineHeight = 1.5;
            fontWeight = '500';
            break;
        case 'label-large-light':
            fontSize = 16;
            lineHeight = 1.5;
            fontWeight = '400';
            break;
        case 'label-large-bold':
            fontSize = 16;
            lineHeight = 1.5;
            fontWeight = '600';
            break;
        case 'label-small':
            fontSize = 13;
            lineHeight = 1.4;
            fontWeight = '400';
            break;
        case 'label-small-bold':
            fontSize = 13;
            lineHeight = 1.4;
            fontWeight = '600';
            break;
        case 'label-enclosed':
            fontSize = 14;
            lineHeight = 1.25;
            fontWeight = '500';
            break;
        case 'label-enclosed-bold':
            fontSize = 14;
            lineHeight = 1.25;
            fontWeight = '600';
            break;
        case 'label-enclosed-small-bold':
            fontSize = 12;
            lineHeight = 1.25;
            fontWeight = '600';
            break;
        case 'button':
            fontSize = 14;
            lineHeight = 1.25;
            fontWeight = '500';
            break;
        case 'button-small':
            fontSize = 12;
            lineHeight = 1.2;
            fontWeight = '500';
            break;
        case 'tooltip-title':
            fontSize = 12;
            lineHeight = 1.5;
            fontWeight = '600';
            break;
        case 'tooltip-body':
            fontSize = 20;
            lineHeight = 1.2;
            fontWeight = '500';
            break;
    }
    return (
        <TypographyBase
            component="div"
            align={align}
            noWrap={noWrap}
            whiteSpace={whiteSpace}
            sx={{
                fontSize,
                lineHeight,
                fontWeight,
                color: (theme) => (color === 'inherit' ? 'inherit' : theme.resqPalette.text[color]),
                ...sx,
            }}
        >
            {children}
        </TypographyBase>
    );
};
